import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import FlowItem from "./flow-item";
import FlowItemQuestion from "./flow-item-question";
import type { useFormBuilderState } from "./hooks/use-form-builder-state";

interface FormBuilderFlowTabProps {
	formState: ReturnType<typeof useFormBuilderState>;
	isLoading?: boolean;
}

export default function FormBuilderFlowTab({ formState, isLoading = false }: FormBuilderFlowTabProps) {
	const { sections, questions, selectedSectionId, selectSection, toggleSection, addQuestion, removeQuestion, reorderQuestions, updateQuestion } = formState;

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;

		if (over && active.id !== over.id) {
			reorderQuestions(active.id as string, over.id as string);
		}
	};

	const handleAddQuestion = () => {
		const newQuestionId = addQuestion();
		// Focus the title field after a short delay to allow render
		setTimeout(() => {
			const titleInput = document.getElementById(`question-title-${newQuestionId}`);
			titleInput?.focus();
		}, 100);
	};

	// Get sections by type for ordered rendering
	const ratingSection = sections.find((s) => s.type === ("rating" as typeof s.type));
	const welcomeSection = sections.find((s) => s.type === ("welcome" as typeof s.type));
	const personalDetailSection = sections.find((s) => s.type === ("personal-detail" as typeof s.type));
	const companyDetailSection = sections.find((s) => s.type === ("company-detail" as typeof s.type));
	const consentSection = sections.find((s) => s.type === ("consent" as typeof s.type));
	const thankYouSection = sections.find((s) => s.type === ("thank-you" as typeof s.type));
	const negativeFeedbackSection = sections.find((s) => s.type === "negative-feedback");
	const feedbackThankYouSection = sections.find((s) => s.type === "feedback-thank-you");

	const questionIds = questions.map((q) => q.id);

	return (
		<div className="space-y-6">
			{/* Rating - Branch point */}
			<div className="relative">
				{/* Short connector below rating */}
				<div className="absolute left-[5px] top-6 h-6 w-0.5 bg-gray-200" />
				<div className="space-y-1">
					{ratingSection && <FlowItem section={ratingSection} isSelected={selectedSectionId === ratingSection.id} onSelect={() => selectSection(ratingSection.id)} disabled={isLoading} />}
				</div>
			</div>

			{/* High Rating Path */}
			<div className="relative">
				{/* Vertical connector line */}
				<div className="absolute left-[5px] top-12 bottom-4 w-0.5 bg-gray-200" />

				<div className="space-y-1">
					{/* Section header */}
					<div className="pb-1">
						<div className="flex items-center gap-2">
							<h3 className="text-xs font-semibold uppercase tracking-wide text-tertiary">High Rating Path</h3>
							<span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Ratings 4 stars and above</span>
						</div>
					</div>

					{/* Welcome - Fixed after rating */}
					{welcomeSection && (
						<FlowItem
							section={welcomeSection}
							isSelected={selectedSectionId === welcomeSection.id}
							onSelect={() => selectSection(welcomeSection.id)}
							onToggle={() => toggleSection(welcomeSection.id)}
							disabled={isLoading}
						/>
					)}

					{/* Questions section header */}
					<div className="pl-6 pt-3 pb-1">
						<h3 className="text-xs font-semibold uppercase tracking-wide text-tertiary">Questions</h3>
					</div>

					{/* Questions */}
					<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
						<SortableContext items={questionIds} strategy={verticalListSortingStrategy}>
							{questions.map((question) => (
								<FlowItemQuestion
									key={question.id}
									question={question}
									isSelected={selectedSectionId === question.id}
									onSelect={() => selectSection(question.id)}
									onDelete={() => removeQuestion(question.id)}
									onFormatChange={(format) => updateQuestion(question.id, { format })}
									canDelete={questions.length > 1}
									disabled={isLoading}
								/>
							))}
						</SortableContext>
					</DndContext>

					{/* Add Question button - styled with dashed circle */}

					<button
						type="button"
						onClick={handleAddQuestion}
						disabled={isLoading}
						className="mb-2 -ml-3.5 rounded-lg flex items-center gap-4 pr-3 bg-white border border-gray-100 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed py-2 px-2 text-tertiary hover:text-primary cursor-pointer w-[calc(100%+28px)] group relative z-10"
					>
						<div className="rounded-full w-6 h-6 bg-white text-gray-400 group-hover:text-gray-700 transition-colors">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="-0.5 -0.5 24 24"
								fill="none"
								stroke="currentColor"
								stroke-linecap="round"
								stroke-linejoin="round"
								id="Circle-Plus--Streamline-Tabler"
								className="w-6 h-6"
							>
								<desc>Circle Plus Streamline Icon: https://streamlinehq.com</desc>
								<path d="M2.875 11.5a8.625 8.625 0 1 0 17.25 0 8.625 8.625 0 0 0 -17.25 0" stroke-width="1"></path>
								<path d="M8.625 11.5h5.75" stroke-width="1"></path>
								<path d="M11.5 8.625v5.75" stroke-width="1"></path>
							</svg>
						</div>
						Add Question
					</button>

					{/* Personal detail - Fixed */}
					{personalDetailSection && (
						<FlowItem
							section={personalDetailSection}
							isSelected={selectedSectionId === personalDetailSection.id}
							onSelect={() => selectSection(personalDetailSection.id)}
							disabled={isLoading}
						/>
					)}

					{/* Company detail - Toggleable */}
					{companyDetailSection && (
						<FlowItem
							section={companyDetailSection}
							isSelected={selectedSectionId === companyDetailSection.id}
							onSelect={() => selectSection(companyDetailSection.id)}
							onToggle={() => toggleSection(companyDetailSection.id)}
							disabled={isLoading}
						/>
					)}

					{/* Consent - Fixed */}
					{consentSection && (
						<FlowItem section={consentSection} isSelected={selectedSectionId === consentSection.id} onSelect={() => selectSection(consentSection.id)} disabled={isLoading} />
					)}

					{/* Thank you - Fixed */}
					{thankYouSection && (
						<FlowItem section={thankYouSection} isSelected={selectedSectionId === thankYouSection.id} onSelect={() => selectSection(thankYouSection.id)} disabled={isLoading} />
					)}
				</div>
			</div>

			{/* Negative Feedback section - separate flow */}
			<div className="relative">
				{/* Vertical connector line */}
				<div className="absolute left-1.25 top-12 bottom-4 w-0.5 bg-gray-200" />

				<div className="space-y-1">
					{/* Section header */}
					<div className="pb-1">
						<div className="flex items-center gap-2">
							<h3 className="text-xs font-semibold uppercase tracking-wide text-tertiary">Low Rating Path</h3>
							<span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">Ratings under 4 stars</span>
						</div>
					</div>

					{negativeFeedbackSection && (
						<div>
							<FlowItem
								section={negativeFeedbackSection}
								isSelected={selectedSectionId === negativeFeedbackSection.id}
								onSelect={() => selectSection(negativeFeedbackSection.id)}
								disabled={isLoading}
							/>
						</div>
					)}

					{/* Feedback Thank you - Fixed after negative feedback */}
					{feedbackThankYouSection && (
						<FlowItem
							section={feedbackThankYouSection}
							isSelected={selectedSectionId === feedbackThankYouSection.id}
							onSelect={() => selectSection(feedbackThankYouSection.id)}
							disabled={isLoading}
						/>
					)}
				</div>
			</div>
		</div>
	);
}
